import { useEditorState } from "@tiptap/react";
import type { Editor } from "@tiptap/react";
import { Button } from "@heroui/react";
import {
  Bold,
  Italic,
  Underline,
  Palette,
  Highlighter,
  AlignRight,
  AlignCenter,
  AlignLeft,
  AlignJustify,
} from "lucide-react";
import { useEditorToolbar, COLORS, HIGHLIGHT_COLORS, FONT_SIZES } from "./useEditorToolbar";
import { EditorToolbarColorPicker } from "./EditorToolbarColorPicker";

type Props = {
  editor: Editor | null;
};

export function EditorToolbar({ editor }: Props) {
  const {
    colorOpen,
    setColorOpen,
    highlightOpen,
    setHighlightOpen,
    toggleBold,
    toggleItalic,
    toggleUnderline,
    setColor,
    setHighlight,
    setFontSize,
    setAlign,
  } = useEditorToolbar(editor);

  const editorState = useEditorState({
    editor,
    selector: (ctx) => ({
      isBold: ctx.editor?.isActive("bold") ?? false,
      isItalic: ctx.editor?.isActive("italic") ?? false,
      isUnderline: ctx.editor?.isActive("underline") ?? false,
      alignRight: ctx.editor?.isActive({ textAlign: "right" }) ?? false,
      alignCenter: ctx.editor?.isActive({ textAlign: "center" }) ?? false,
      alignLeft: ctx.editor?.isActive({ textAlign: "left" }) ?? false,
      alignJustify: ctx.editor?.isActive({ textAlign: "justify" }) ?? false,
    }),
  });

  if (!editor) return null;

  const activeClass = "bg-default-200";

  return (
    <div dir="rtl" className="flex flex-wrap gap-1 border-b border-default-200 p-2 bg-content1 sticky top-0 z-10">
      <Button
        size="sm"
        variant="light"
        isIconOnly
        onPress={toggleBold}
        className={editorState?.isBold ? activeClass : ""}
        aria-label="Bold"
        aria-pressed={editorState?.isBold}
      >
        <Bold size={16} />
      </Button>
      <Button
        size="sm"
        variant="light"
        isIconOnly
        onPress={toggleItalic}
        className={editorState?.isItalic ? activeClass : ""}
        aria-label="Italic"
        aria-pressed={editorState?.isItalic}
      >
        <Italic size={16} />
      </Button>
      <Button
        size="sm"
        variant="light"
        isIconOnly
        onPress={toggleUnderline}
        className={editorState?.isUnderline ? activeClass : ""}
        aria-label="Underline"
        aria-pressed={editorState?.isUnderline}
      >
        <Underline size={16} />
      </Button>

      <span className="w-px bg-default-200 self-stretch mx-1" aria-hidden />

      <EditorToolbarColorPicker
        isOpen={colorOpen}
        onOpenChange={setColorOpen}
        colors={COLORS}
        onSelect={setColor}
        trigger={<Palette size={16} />}
        ariaLabel="Text color"
      />
      <EditorToolbarColorPicker
        isOpen={highlightOpen}
        onOpenChange={setHighlightOpen}
        colors={HIGHLIGHT_COLORS}
        onSelect={setHighlight}
        trigger={<Highlighter size={16} />}
        ariaLabel="Highlight color"
        showTransparentLabel
      />

      <span className="w-px bg-default-200 self-stretch mx-1" aria-hidden />

      <select
        aria-label="Font size"
        className="text-sm border border-default-200 rounded-lg px-1 py-0.5 bg-content1 text-foreground focus:outline-none"
        onChange={(e) => setFontSize(e.target.value)}
        defaultValue="12"
      >
        {FONT_SIZES.map((s) => (
          <option key={s} value={s}>{s}px</option>
        ))}
      </select>

      <span className="w-px bg-default-200 self-stretch mx-1" aria-hidden />

      <Button size="sm" variant="light" isIconOnly onPress={() => setAlign("right")} className={editorState?.alignRight ? activeClass : ""} aria-label="Align right" aria-pressed={editorState?.alignRight}>
        <AlignRight size={16} />
      </Button>
      <Button size="sm" variant="light" isIconOnly onPress={() => setAlign("center")} className={editorState?.alignCenter ? activeClass : ""} aria-label="Align center" aria-pressed={editorState?.alignCenter}>
        <AlignCenter size={16} />
      </Button>
      <Button size="sm" variant="light" isIconOnly onPress={() => setAlign("left")} className={editorState?.alignLeft ? activeClass : ""} aria-label="Align left" aria-pressed={editorState?.alignLeft}>
        <AlignLeft size={16} />
      </Button>
      <Button size="sm" variant="light" isIconOnly onPress={() => setAlign("justify")} className={editorState?.alignJustify ? activeClass : ""} aria-label="Justify" aria-pressed={editorState?.alignJustify}>
        <AlignJustify size={16} />
      </Button>
    </div>
  );
}
